import type { ReactElement } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import type { AxiosInstance } from 'axios'
import { SnackbarProvider } from '@mysaiva/core/components'
import { AccessGate, DeveloperPlatformGate } from './guards/DeveloperPlatformGate'
import { DeveloperPlatformProviders } from './providers/DeveloperPlatformProviders'
import { ModeProvider } from './theme/ModeProvider'
import { DeveloperPlatformLayout } from './layout/DeveloperPlatformLayout'
import { DashboardPage } from './pages/dashboard/DashboardPage'
import { ApiKeysPage } from './pages/api-keys/ApiKeysPage'
import { ApiKeyDetailPage } from './pages/api-keys/ApiKeyDetailPage'
import { ApiKeysClientProvider } from './api-keys/ApiKeysClientProvider'
import { ApiOpenApiPreviewPage } from './pages/docs/openapi-preview/ApiOpenApiPreviewPage'
import { ApiOverviewPage } from './pages/docs/overview/ApiOverviewPage'
import { LegacyDocsRedirect } from './pages/docs/LegacyDocsRedirect'
import { DeveloperPlatformAnalytics } from './analytics/DeveloperPlatformAnalytics'
import { MigrationNotice } from './layout/MigrationNotice'
import { WORKSPACE_ANALYTICS_ENABLED } from './constants'

interface DeveloperPlatformRoutesProps {
  apiClient: AxiosInstance
}

/**
 * Everything under `/integrations`. Mounted from the shell's `App.tsx` with
 * `path="/integrations/*"`, so all paths here are relative.
 *
 * Unknown sub-paths fall back to the dashboard: the workspace must never
 * render a blank page (SAIV-11348).
 */
export function DeveloperPlatformRoutes({ apiClient }: DeveloperPlatformRoutesProps): ReactElement {
  return (
    // ModeProvider sits outside the gates and providers: their loading pages
    // (e.g. core's OrgProvider while an organisation switch loads) would
    // otherwise unmount it and flash a white page in dark mode (SAIV-11635).
    <ModeProvider>
      <DeveloperPlatformGate>
        <DeveloperPlatformProviders apiClient={apiClient}>
          <AccessGate>
            <SnackbarProvider>
              {WORKSPACE_ANALYTICS_ENABLED && <DeveloperPlatformAnalytics />}
              <MigrationNotice />
              <ApiKeysClientProvider>
                <Routes>
                  <Route element={<DeveloperPlatformLayout />}>
                    <Route index element={<DashboardPage />} />
                    <Route path="api-keys" element={<ApiKeysPage />} />
                    <Route path="api-keys/:keyId" element={<ApiKeyDetailPage />} />
                    <Route path="docs/overview/:sectionId?" element={<ApiOverviewPage />} />
                    <Route path="docs/openapi/:resourceId?" element={<ApiOpenApiPreviewPage />} />
                    {/* Links to the retired hand-written docs (PR #75). */}
                    <Route path="docs/resources/:resourceId?" element={<LegacyDocsRedirect />} />
                    <Route path="docs/reference/*" element={<LegacyDocsRedirect />} />
                    <Route path="*" element={<Navigate to="." replace />} />
                  </Route>
                </Routes>
              </ApiKeysClientProvider>
            </SnackbarProvider>
          </AccessGate>
        </DeveloperPlatformProviders>
      </DeveloperPlatformGate>
    </ModeProvider>
  )
}
